import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const EditParent = () => {
  const { api, user } = useAuth();
  const { parentId } = useParams();
  const { t } = useTranslation('editparent');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    id_number: '',
    relation_type: '',
    address: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/parents/${parentId}`);
        
        // Ensure no null values in the form data
        const parentData = response.data.parent;
        const sanitizedData = {
          name: parentData.name || '',
          email: parentData.email || '',
          phone: parentData.phone || '',
          address: parentData.address || '',
          id_number: parentData.id_number || '',
          relation_type: parentData.relation_type || '',
        };
        
        setFormData(sanitizedData);
      } catch (err) {
        toast.error(t('alerts.loadFailed'), {
          position: 'top-center'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [parentId, user.school_reg, api, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.patch(
        `/api/v1/admin/schools/${user.school_reg}/parents/${parentId}`,
        formData
      );
      toast.success(t('alerts.updateSuccess'), {
        position: 'top-center'
      });
    } catch (err) {
      toast.error(t('alerts.updateFailed'), {
        position: 'top-center'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const rtlClass = document.documentElement.dir === 'rtl' ? 'rtl text-right' : '';

  return (
    <>
    <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        }}
      />
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className={`bg-white rounded-lg shadow-lg w-full ${rtlClass}`}>
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">{t('pageTitle')}</h2>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.fullName')}</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('form.fullNamePlaceholder')}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${rtlClass}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('form.emailPlaceholder')}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${rtlClass}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.phoneNumber')}</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('form.phoneNumberPlaceholder')}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${rtlClass}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.idNumber')}</label>
                <input
                  name="id_number"
                  value={formData.id_number}
                  onChange={handleChange}
                  placeholder={t('form.idNumberPlaceholder')}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${rtlClass}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.relationshipType')}</label>
                <select
                  name="relation_type"
                  value={formData.relation_type}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${rtlClass}`}
                >
                  <option value="">{t('form.relationshipSelect')}</option>
                  <option value="Father">{t('form.father')}</option>
                  <option value="Mother">{t('form.mother')}</option>
                  <option value="Guardian">{t('form.guardian')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('form.address')}</label>
                <input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t('form.addressPlaceholder')}
                  required
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${rtlClass}`}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saving}
                className="w-full md:w-auto bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? t('button.saving') : t('button.saveChanges')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default EditParent;